import React, { useContext } from 'react';

import { ISettingsParam, ISettingsValue } from '@wix/tpa-settings';

import { ControllerProps } from '../components/ControllerValidation';

/**
 * We have to introduce WidgetSettingsContext because of next.
 * Standard `useSettings` hook from the tpa-settings library,
 * in case of ML sites takes settings from the current language.
 * In the same time there is no way to display settings pannel in
 * non-primary language: https://wix.atlassian.net/browse/EP-5929
 *
 * This causes settings, which are not text (booleans, numbers, string enums)
 * to be inconsistent. They 'froze' in default state.
 *
 * This context has current language and primary language settings.
 * `get` method takes `lang` parameter, which can be 'current' or 'primary'.
 *
 * We use 'current' only for texts that should be displayed,
 * all other values should be taken from 'primary' language.
 *
 * Still there is no way for user to change string values in
 * the settings pannel for non-primary language.
 */

type WidgetSettingsContextType = {
  /**
   * TODO: maybe is possible re-create types of original `useSettings` hook and
   * add type to the `.get` method
   **/
  get: (param: ISettingsParam, lang?: 'current' | 'primary') => ISettingsValue;
};

export const WidgetSettingsContext =
  React.createContext<WidgetSettingsContextType>({
    get: () => null,
  });

type WidgetSettingsProviderProps = { settings: ControllerProps['settings'] };

export const WidgetSettingsProvider: React.FC<WidgetSettingsProviderProps> = ({
  settings,
  children,
}) => {
  const allSettings = {
    get: (
      param: ISettingsParam,
      lang: keyof WidgetSettingsProviderProps['settings'] = 'current',
    ) => settings[lang][param.key],
  };

  return (
    <WidgetSettingsContext.Provider value={allSettings}>
      {children}
    </WidgetSettingsContext.Provider>
  );
};

export function useWidgetSettings() {
  return useContext(WidgetSettingsContext);
}
